import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { DateTime } from 'luxon';
import type { InsertOneResult, UpdateResult } from 'mongodb';
import { WorkTrackingChange, WorkTrackingValDiff } from './WorkTrackingChange.js';
import { WorkTrackingEntry } from './WorkTrackingEntry.js';
import { WorkTrackingTaetigkeitenState } from './WorkTrackingTaetigkeitenState.js';

export enum WorkTrackingAuslagenTypes {
  BUEROBEDARF = 'Bürobedarf',
  FAHRTKOSTEN = 'Fahrtkosten',
  LEHRMEDIUM = 'Lehrmittel',
  PORTO = 'Porto',
  TELEFONKOSTEN = 'Telefonkosten',
  VERANSTALTUNGSKOSTEN = 'Veranstaltungskosten Lernforderung',
  WERBEKOSTEN = 'Werbekosten',
  SONSTIGE_AUSGABEN = 'Sonstige Ausgaben Lernforderung',
  SONSTIGE_KOSTEN = 'Sonstige Kosten',
}

export function WorkTrackingAuslagenTypeToKostenart(type: WorkTrackingAuslagenTypes): string {
  switch (type) {
    case WorkTrackingAuslagenTypes.BUEROBEDARF:
      return '46600000';
    case WorkTrackingAuslagenTypes.FAHRTKOSTEN:
      return '47100000';
    case WorkTrackingAuslagenTypes.LEHRMEDIUM:
      return '49100000';
    case WorkTrackingAuslagenTypes.PORTO:
      return '49200000';
    case WorkTrackingAuslagenTypes.TELEFONKOSTEN:
      return '47300000';
    case WorkTrackingAuslagenTypes.VERANSTALTUNGSKOSTEN:
      return '46000000';
    case WorkTrackingAuslagenTypes.WERBEKOSTEN:
      return '47200000';
    case WorkTrackingAuslagenTypes.SONSTIGE_AUSGABEN:
      return '49000000';
    case WorkTrackingAuslagenTypes.SONSTIGE_KOSTEN:
      return '49300000';
  }
}

@Entity()
export class WorkTrackingAuslage extends MongoEntity {
  static override _class = 'WorkTrackingAuslage';
  static override _collectionName = 'work-tracking-auslagen';

  constructor(doc: PartialEntityData<WorkTrackingAuslage>) {
    super(doc);
  }

  static override async afterCreate(entity: WorkTrackingAuslage, result: InsertOneResult<Document>) {
    await super.afterCreate(entity, result);

    if (entity.kpfID && entity.userID && entity.date) {
      await WorkTrackingEntry.addMentionedKPF(entity.userID, entity._id + '_' + entity.kpfID, entity.date);
    }
  }

  static override async afterUpdate(
    oldEntity: WorkTrackingAuslage,
    newEntity: WorkTrackingAuslage,
    result: UpdateResult,
  ) {
    await super.afterUpdate(oldEntity, newEntity, result);

    if (newEntity.kpfID && newEntity.userID && newEntity.date) {
      await WorkTrackingEntry.addMentionedKPF(newEntity.userID, newEntity._id + '_' + newEntity.kpfID, newEntity.date);
    } else if (oldEntity.kpfID && !newEntity.kpfID && oldEntity.userID && oldEntity.date) {
      await WorkTrackingEntry.removeMentionedKPF(
        oldEntity.userID,
        oldEntity._id + '_' + oldEntity.kpfID,
        oldEntity.date,
      );
    }
  }

  static override async afterRemove(id: string, entityData: EntityData<WorkTrackingAuslage>): Promise<void> {
    super.afterRemove(id, entityData);

    if (entityData.kpfID && entityData.userID && entityData.date) {
      await WorkTrackingEntry.removeMentionedKPF(entityData.userID, id + '_' + entityData.kpfID, entityData.date);
    }
  }

  @DataLoadedField(type => Mitarbeiter, ({ userID }) => userID)
  user: Maybe<Mitarbeiter>;

  @Field(type => String)
  userID: Maybe<string>;

  @Field(type => String)
  stateContractor: Maybe<WorkTrackingTaetigkeitenState>;

  @Field(type => String)
  stateResponsible: Maybe<WorkTrackingTaetigkeitenState>;

  get state() {
    if (
      this.stateContractor === WorkTrackingTaetigkeitenState.QUESTION ||
      this.stateResponsible === WorkTrackingTaetigkeitenState.QUESTION
    ) {
      return WorkTrackingTaetigkeitenState.QUESTION;
    }
    if (
      this.stateContractor === WorkTrackingTaetigkeitenState.OPEN ||
      this.stateResponsible === WorkTrackingTaetigkeitenState.OPEN ||
      !this.stateResponsible ||
      !this.stateContractor
    ) {
      return WorkTrackingTaetigkeitenState.OPEN;
    }
    return WorkTrackingTaetigkeitenState.FINISHED;
  }

  @Field(type => DateTime)
  date: Maybe<DateTime>;

  @Field(type => Float)
  money: Maybe<number>;

  @DataLoadedField(type => KPF, ({ kpfID }) => kpfID)
  kpf: Maybe<KPF>;

  @Field(type => String)
  kpfID: Maybe<string>;

  @Field(type => String)
  note: Maybe<string>;

  @Field(type => String)
  type: Maybe<string>;

  @Field(type => [WorkTrackingChange])
  changes: Maybe<WorkTrackingChange[]>;

  @Field(type => String)
  public receiptID: Maybe<string>;

  @DataLoadedField(type => AdornisFile, ({ receiptID }) => receiptID)
  public receipt: Maybe<AdornisFile>;

  get lastNote() {
    if (!this.changes || this.changes.length === 0) {
      return '';
    }

    return this.changes[this.changes.length - 1]?.note ?? '';
  }

  static fieldNames(field: string) {
    return (
      {
        generatedFromID: 'generatedFromID',
        changes: 'changes',

        userID: 'NutzerID',
        stateContractor: 'Tätiger',
        stateResponsible: 'Verwaltung',
        date: 'Datum',
        kpfID: 'KPF',
        money: '€',
        note: 'Was',
      }[field] ?? 'Unbekannt'
    );
  }

  /**
   * Check if the work tracking auslage is the same as another and if not add the changes to the changes array.
   * @param userID User ID of the user who made the changes.
   * @param oldState The old state of the work tracking taeigkeit.
   * @param note Optional note to add to the changes.
   */
  addChanges(userID: string, oldState: WorkTrackingAuslage, note?: Maybe<string>) {
    const diffs: WorkTrackingValDiff[] = [];

    if (this.money !== oldState.money) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'money',
          from: oldState.money?.toString(),
          to: this.money?.toString(),
        }),
      );
    }

    if (this.note !== oldState.note) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'note',
          from: oldState.note,
          to: this.note,
        }),
      );
    }

    if (oldState.date && !this.date?.equals(oldState.date)) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'date',
          from: oldState.date?.toISO(),
          to: this.date?.toISO(),
        }),
      );
    }

    if (diffs.length > 0 || note) {
      if (!this.changes) {
        this.changes = [];
      }

      if (this.userID === userID) {
        this.stateContractor = WorkTrackingTaetigkeitenState.FINISHED;
        this.stateResponsible = WorkTrackingTaetigkeitenState.QUESTION;
      } else {
        this.stateResponsible = WorkTrackingTaetigkeitenState.FINISHED;
        this.stateContractor = WorkTrackingTaetigkeitenState.QUESTION;
      }

      this.changes.push(
        new WorkTrackingChange({
          note: note ?? '',
          fromID: userID,
          date: DateTime.now().toJSDate(),
          diff: diffs,
        }),
      );
    }
  }

  /**
   * Change the state of the work tracking auslage and add a change to the changes array.
   */
  changeState(userID: string, newState: WorkTrackingTaetigkeitenState, note?: string) {
    if (!this.changes) {
      this.changes = [];
    }

    // * Check if the state is already the same.
    if (this.userID === userID && this.stateContractor === newState) {
      return;
    }
    if (this.userID !== userID && this.stateResponsible === newState) {
      return;
    }

    // * Add the change to the changes array.
    this.changes.push(
      new WorkTrackingChange({
        fromID: userID,
        note,
        date: DateTime.now().toJSDate(),
        diff: [
          new WorkTrackingValDiff({
            val: this.userID === userID ? 'stateContractor' : 'stateResponsible',
            from: this.userID === userID ? this.stateContractor : this.stateResponsible,
            to: newState,
          }),
        ],
      }),
    );

    // * Change the state.
    if (userID === this.userID) {
      this.stateContractor = newState;
    } else {
      this.stateResponsible = newState;
    }
  }
}
